import classNames from "classnames";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { PhoneInputSet } from "../../../components/inputs/phoneInputSet";
import { TabNav } from "../../../components/tabNav";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useUpdateProfileMutation } from "../../../redux/features/auth/auth.api";
import { authSliceActions } from "../../../redux/features/auth/auth.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { OrganizationData } from "../../../utils/types";
import { UpdateProfileSchema } from "../../../utils/validations";
import { OrganizationForm } from "../../settings/profileSettings";

export const AddOrgPage = () => {
  const navigate = useNavigate();

  const [orgData, setOrgData] = useState<OrganizationData>();
  const [isOrgDataValid, setIsOrgDataValid] = useState(false);

  const { profile } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();

  const [updateProfile, { isLoading: updatingProfile }] =
    useUpdateProfileMutation();

  const {
    values,
    handleSubmit,
    setFieldValue,
    isValid,
    handleBlur,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      firstName: profile?.firstName ?? "",
      lastName: profile?.lastName ?? "",
      email: profile?.email ?? "",
      phoneNumber: profile?.phoneNumber ?? "",
      isOrganization: !!profile?.isOrganisation,
    },
    enableReinitialize: true,
    validationSchema: UpdateProfileSchema,
    onSubmit: async (vals) => {
      let mainOrgData = undefined;

      if (vals.isOrganization && orgData) {
        const { isOrganisation, website, ...other } = orgData;
        mainOrgData = other;
      }

      const resp = await updateProfile({
        firstName: vals.firstName,
        lastName: vals.lastName,
        phoneNumber: vals.phoneNumber || undefined,
        twitterLink: profile?.twitterLink || undefined,
        linkedinLink: profile?.linkedinLink || undefined,
        instagramLink: profile?.instagramLink || undefined,
        tiktokLink: profile?.tiktokLink || undefined,
        facebookLink: profile?.facebookLink || undefined,
        website: orgData?.website || undefined,
        isOrganisation: vals.isOrganization,
        organisation: mainOrgData
          ? {
              ...mainOrgData,
              logo: mainOrgData?.logo || undefined,
              phoneNumber: mainOrgData?.phoneNumber || undefined,
            }
          : undefined,
      });

      if (resp.data?.status === "success") {
        dispatch(authSliceActions.markHasCompletedProfile(true));
        navigate("/event/new", { replace: true });
      }
    },
  });

  return (
    <div className="flex w-full justify-center">
      <div className="mx-auto flex w-full max-w-[500px] flex-col gap-3">
        <BgWrapper className="flex w-full flex-col items-center justify-center gap-4 !py-10">
          <div className="mx-auto w-full max-w-[320px] text-center">
            <p className="text-32 max-w-[300px] font-fraunces !text-[32px] font-medium leading-[100%]">
              Before you create your first event...
            </p>
            <p className="mt-2 max-w-[320px] text-grey-600">
              Let us know how you’d like to create your event and the next ones.
              You can always change this later in settings
            </p>
          </div>

          <TabNav
            navs={[
              {
                title: "Organization",
                to: "organization",
                onClick: () => {
                  setFieldValue("isOrganization", true);
                },
              },
              {
                title: "Individual",
                to: "individual",
                onClick: () => {
                  setFieldValue("isOrganization", false);
                },
              },
            ]}
            activeTab={values.isOrganization ? "organization" : "individual"}
            variant="switcher"
            tabClassName=""
            className="w-full"
          />

          {values.isOrganization ? (
            <OrganizationForm
              isOrg={values.isOrganization}
              handleDataChange={(data, isDataValid) => {
                setOrgData(data);
                setIsOrgDataValid(isDataValid);
              }}
              imageContainerClassName="order-last"
            />
          ) : (
            <>
              <div className="flex w-full gap-2 border-b border-grey-100 py-4">
                <div className="flex-1">
                  <p className="text-base font-bold text-primary">
                    {profile?.firstName} {profile?.lastName}
                  </p>
                  <p className="text-sm text-grey-900">{profile?.email}</p>
                </div>

                <ProfileIcon dimension={40} className="!p-[10px]" />
              </div>

              <PhoneInputSet
                className="col-span-full w-full"
                value={values.phoneNumber}
                onChange={(v) => {
                  setFieldValue("phoneNumber", v);
                }}
                onBlur={handleBlur}
                errors={
                  errors.phoneNumber && touched.phoneNumber
                    ? errors.phoneNumber
                    : ""
                }
                name="phoneNumber"
              />

              <p className="text-base text-grey-600">
                You’d be creating events as an individual, you can always change
                this later.
              </p>
            </>
          )}

          <div
            className={classNames("flex w-full flex-col", {
              "mt-4": values.isOrganization,
            })}
          >
            <Button
              type="button"
              onClick={() => {
                handleSubmit();
              }}
              text="Continue"
              variation="primary"
              disabled={
                (values.isOrganization && !isOrgDataValid) ||
                (!values.isOrganization && !isValid) ||
                updatingProfile
              }
              loading={updatingProfile}
            />

            <Button
              type="button"
              onClick={() => {
                navigate("/events");
              }}
              text="Go Back to Events"
              variation="bland"
              className="!text-grey-500"
            />
          </div>
        </BgWrapper>
      </div>
    </div>
  );
};
